import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { USER_AUTHINFOS } from 'manager/http-constants_key';
import { SESSION_KEY } from 'src/app/shared/constant/session-constants';
import { map } from 'rxjs/operators';
import { MESSAGE_CODE } from 'src/app/shared/constant/message-constant';
import { CommonService } from './common.service';
import {
  MessageData,
  ToastMessageData,
} from '../html-parts/message-common/message-data';
import { TOAST } from '../constant/primeng-constants';

const credentialsKey = 'access_token';

@Injectable({
  providedIn: 'root',
})

/**
 * ログイン処理
 */
export class LoginService {
  private _currentUser: any;
  private _credentials: any;
  constructor(
    private http: HttpClient,
    private commonService: CommonService,
    private messageData: MessageData
  ) {
    this.getCredentialsFromLocalStorage();
    const self = this;
    window.addEventListener('storage', function (e) {
      const savedCredentials = localStorage.getItem(credentialsKey);
      if (!savedCredentials) {
        self.logout();
      }
    });
  }

  /**
   * ログイン処理
   */
  public login(): void {
    sessionStorage.clear();
    localStorage.clear();

    this.setCurrentUser(null);

    window.location.href = window.location.origin;
  }

  /**
   * ログアウト処理
   * @param messageCode メッセージコード
   */
  public logout(messageCode?: string): void {
    // セッションに保存したログインユーザ情報を削除
    sessionStorage.clear();
    localStorage.clear();

    this.setCurrentUser(null);

    // メッセージコードが存在するか否か
    if (!messageCode) {
      // メッセージコードが存在しない場合

      // メッセージコード(ログアウトしました)を標準セット
      messageCode = MESSAGE_CODE.N90000;
    }

    // セッションにログアウト画面用のメッセージコードを格納
    window.sessionStorage.setItem(SESSION_KEY.loginOutMessageCode, messageCode);

    // Auth0のログアウト 及び ログアウト後にメッセージ画面へ遷移
    window.location.href = window.location.origin + '/pages/message';
  }

  getCredentialsFromLocalStorage() {
    const savedCredentials = localStorage.getItem(credentialsKey);
    if (savedCredentials) {
      this._credentials = savedCredentials;
    }
  }

  /**
   * ログインユーザ情報取得
   * @returns JSONデータ
   */
  public getLoginUser(): Observable<any> {
    // ログインユーザ情報を返却する
    return this.http
      .get(this.commonService.url(USER_AUTHINFOS), { observe: 'response' })
      .pipe(map((res: HttpResponse<any>) => res));
  }

  /**
   * ログインメッセージ
   * @param loginUserName ログインユーザ名
   */
  public loginMessage(loginUserName?: string): void {
    // ログインメッセージを出力
    this.messageData.toastMessage(
      new ToastMessageData({
        severity: TOAST.INFO,
        summary: this.commonService.msg(MESSAGE_CODE.N80000),
        detail: this.commonService.msg(MESSAGE_CODE.N80001, loginUserName),
        position: TOAST.TOP_RIGHT,
        life: 5000,
      })
    );
    sessionStorage.setItem("loginState", "1");
  }

  /**
   * ログアウトメッセージ
   */
  public logoutMessage(): void {
    // ログアウトメッセージを出力
    this.messageData.toastMessage(
      new ToastMessageData({
        severity: TOAST.INFO,
        summary: this.commonService.msg(MESSAGE_CODE.N80002),
        detail: this.commonService.msg(MESSAGE_CODE.N80003),
        position: TOAST.TOP_RIGHT,
        life: 5000,
      })
    );
  }

  /**
   * Auth0ログインユーザ情報取得
   * @returns ログインユーザ情報
   */
  public getAuth0LoginUser(): Observable<any> {
    // Auth0ログインユーザ情報を返却する
    return this.getCurrentUser();
  }

  /**
   * Auth0ログイン状態取得
   * @returns true:ログイン中,false:ログアウト中
   * ※ セッション切れの場合は、true:ログイン中となる
   */
  public getAuth0LoginState(): any {
    // ログイン状態を返却する
    return !!this.credentials;
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  get credentials(): any {
    return this._credentials;
  }

  /**
   * Auth0ログインユーザトークン
   */
  public getAuth0LoginToken(): Observable<any> {
    // ログインユーザトークンを返却する
    return this.credentials;
  }

  /**
   * Sets the user credentials.
   * Otherwise, the credentials are only persisted for the current session.
   * @param credentials The user credentials.
   */
  public setCredentials(credentials?: string) {
    this._credentials = credentials || null;

    if (credentials) {
      localStorage.setItem(credentialsKey, credentials);
    } else {
      localStorage.removeItem(credentialsKey);
    }
  }

  public getCurrentUser(): any {
    const loginUserText = window.sessionStorage.getItem(
      SESSION_KEY.loginUserInformation
    );

    if (!loginUserText) {
      this._currentUser = null;
      return null;
    }

    try {
      this._currentUser = JSON.parse(loginUserText);
      return this._currentUser;
    } catch (_e) {
      this._currentUser = null;
      window.sessionStorage.removeItem(SESSION_KEY.loginUserInformation);
      return null;
    }
  }

  public setCurrentUser(user: any) {
    this._currentUser = user;

    if (user) {
      window.sessionStorage.setItem(
        SESSION_KEY.loginUserInformation,
        JSON.stringify(user)
      );
      return;
    }

    window.sessionStorage.removeItem(SESSION_KEY.loginUserInformation);
  }

  /**
   * check if loginUser is loaded
   * used on page that requires a login user on initial and header or interceptor might not have finish loading loginUser after page reload
   * will set current login user to service if login state is true
   * @returns true: if there is a current user loaded to service 
   *          true: loaded user but login state is true (this will set current user)
   *          false: no login user and/or login state is false
   */
  public checkLoginUser() {
    return new Promise<any>((resolve) => {
      if (!this.getCurrentUser() && this.getAuth0LoginState()) {
        this.getLoginUser().subscribe((response) => {
          if (this.commonService.checkNoneResponse(response)) {
            resolve(false);
          } else {
            this.setCurrentUser(response.body[0]);
            resolve(true);
          }
        });
      } else {
        resolve(this.getAuth0LoginState());
      }
    });
  }
}
